import { useRef } from 'react';

const MAX_FIRED_KEYS = 100;

/**
 * Fire `fire()` synchronously during render the first time `condition` is
 * true for a given key within a mount. Re-mounts (e.g. Strict-Mode
 * mount/unmount/remount in dev) reset the fired-key set and can fire again,
 * so `fire()` must be idempotent (typically via a singleflight or downstream
 * cache). The fired-key set is bounded for long-lived mounted sessions that
 * observe many distinct keys.
 */
export const useFireOncePerKeyOnRender = (
	condition: boolean,
	key: string | undefined,
	fire: () => void,
): void => {
	const firedKeysRef = useRef<Set<string>>(new Set());
	if (condition && key && !firedKeysRef.current.has(key)) {
		firedKeysRef.current.add(key);
		if (firedKeysRef.current.size > MAX_FIRED_KEYS) {
			const oldestKey = firedKeysRef.current.values().next().value;
			if (oldestKey !== undefined) {
				firedKeysRef.current.delete(oldestKey);
			}
		}
		fire();
	}
};
